import { logUsage } from 'owa-analytics';
import { getMRUAttachmentStore } from 'owa-attachment-suggestions';
import { getGuid } from 'owa-guid';
import {
    AttachmentSuggestions,
    ClientItem,
    mailStore,
    ReplyWithAttachmentSuggestion,
} from 'owa-mail-store';
import {
    lazyLogCachedContentRendered,
    lazyLogClientLayout,
    lazyLogResultsRendered,
    ResultsView,
} from 'owa-search-instrumentation';
import { SubstrateSearchScenario } from 'owa-search-service';
import ItemId from 'owa-service/lib/contract/ItemId';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { DEFAULT_MAXNUMBEROFATTACHMENTS } from './constants';
import extractAttachmentSuggestions from './extractAttachmentSuggestions';

export function logAttachMenuData(
    referenceItemId: ItemId,
    defaultButtonsList: string,
    logicalId: string,
    newLogicalId: string
) {
    const menuData = getAttachMenuData(referenceItemId);

    // Log data now.
    // This datapoint is used in OWA intelligence scorecard.
    // Make sure any change won't break the scorecard
    logUsage('AttachButtonMenuOpened', [...menuData, defaultButtonsList, true /* isMail */]);

    const traceId = getAttachmentSuggestionsTraceId(referenceItemId);
    if (!!traceId) {
        const sessionSettings = getUserConfiguration().SessionSettings;
        lazyLogCachedContentRendered.importAndExecute(
            SubstrateSearchScenario.SuggestedAttachments,
            sessionSettings.UserPuid,
            sessionSettings.ExternalDirectoryTenantGuid,
            logicalId,
            newLogicalId,
            getGuid() /* conversationId */,
            traceId
        );

        logAttachmentSuggestionsClientLayout(referenceItemId, newLogicalId);
    }
}

export function logAttachMenuDataOnClose(referenceItemId: ItemId) {
    const menuData = getAttachMenuData(referenceItemId);

    // Log data now.
    // This datapoint is used in OWA intelligence scorecard.
    // Make sure any change won't break the scorecard
    logUsage('AttachButtonMenuClosed', [true /* isMail */, ...menuData]);
}

function getAttachMenuData(referenceItemId: ItemId) {
    // Get data about attachment suggestions shown.
    const replyWithSuggestions: AttachmentSuggestions = mailStore.items.get(referenceItemId?.Id)
        ?.AttachmentSuggestions;

    const attachmentSuggestions: ReplyWithAttachmentSuggestion[] = extractAttachmentSuggestions(
        replyWithSuggestions,
        getAttachmentSuggestions(referenceItemId)
    );

    const endpoints: number[] = [];
    const ids: string[] = [];
    const ranks: number[] = [];
    const provenances: string[] = [];
    const fileTypes: string[] = [];

    if (attachmentSuggestions) {
        attachmentSuggestions.forEach((item, index) => {
            endpoints.push(item.Endpoint);
            ids.push(item.Id);
            ranks.push(index);
            provenances.push(item.FileSourceType);
            fileTypes.push(item.FileExtension);
        });
    }

    return [
        endpoints.join(','),
        ids.join(','),
        ranks.join(','),
        provenances.join(','),
        fileTypes.join(','),
    ];
}

function getAttachmentSuggestions(referenceItemId: ItemId | null): ReplyWithAttachmentSuggestion[] {
    const referenceItem: ClientItem | null = mailStore.items.get(referenceItemId?.Id);
    const contextualAttachmentSuggestions = referenceItem?.ContextualAttachmentSuggestions;
    const mruSuggestions: ReplyWithAttachmentSuggestion[] | null = getMRUAttachmentStore()
        .attachments;

    return [...(contextualAttachmentSuggestions || []), ...(mruSuggestions || [])];
}

function getAttachmentSuggestionsTraceId(referenceItemId: ItemId | null): string | null {
    const suggestions = getAttachmentSuggestions(referenceItemId);
    const traceId = suggestions.length && suggestions[0]?.TraceId;
    if (traceId) {
        return traceId;
    }

    // if the suggestion list is empty,
    // the trace ID is also available in the item or the MRU attachment store
    const referenceItem: ClientItem | null = mailStore.items.get(referenceItemId?.Id);
    return referenceItem?.ContextualAttachmentSuggestionsTraceId || getMRUAttachmentStore().traceId;
}

export function logAttachmentSuggestionsClientLayout(
    referenceItemId: ItemId | null, // null when new message
    logicalId: string
) {
    // Get data about attachment suggestions shown.
    const attachmentSuggestions: ReplyWithAttachmentSuggestion[] = getAttachmentSuggestions(
        referenceItemId
    );

    const resultsView: ResultsView[] = [];
    attachmentSuggestions.forEach((item, index) => {
        const shown = index < DEFAULT_MAXNUMBEROFATTACHMENTS;
        resultsView.push({
            type: 'Entity',
            name: 'Files',
            referenceId: item?.ReferenceId,
            providerId: item?.TraceId,
            position: shown ? index + 1 : -1, // position starts from 1
            entityType: 'File',
            placementType: shown ? 'Visible' : 'Hidden',
        });
    });

    const sessionSettings = getUserConfiguration().SessionSettings;
    lazyLogClientLayout.importAndExecute(
        SubstrateSearchScenario.SuggestedAttachments,
        sessionSettings.UserPuid,
        sessionSettings.ExternalDirectoryTenantGuid,
        logicalId,
        getAttachmentSuggestionsTraceId(referenceItemId),
        'Vertical',
        resultsView
    );
}

export function logAttachmentMenuSuggestionData(
    combinedAttachmentSuggestions: ReplyWithAttachmentSuggestion[],
    suggestedAttachmentsLogicalId: string,
    suggestedAttachmentsTraceId: string,
    clearSuggestedAttachmentsTraceId: () => void,
    attachMenuOpenTime: number,
    referenceItemId: ItemId
) {
    const currentTraceId =
        combinedAttachmentSuggestions[0]?.TraceId ||
        getAttachmentSuggestionsTraceId(referenceItemId);
    if (!!suggestedAttachmentsTraceId && currentTraceId !== suggestedAttachmentsTraceId) {
        lazyLogResultsRendered.importAndExecute(
            SubstrateSearchScenario.SuggestedAttachments,
            suggestedAttachmentsLogicalId,
            currentTraceId,
            new Date().getTime() - attachMenuOpenTime
        );
        logAttachmentSuggestionsClientLayout(referenceItemId, suggestedAttachmentsLogicalId);
        clearSuggestedAttachmentsTraceId();
    }
}
